/*!
 * externalTape.js — a sound file played by another program, drawn here
 * © 2026 Rob Canning — GPLv3
 *
 *     audio(uid:tape, src:"tape.wav", engine:external)
 *     audio(uid:tape, src:"tape.wav", engine:external, latency:12, speed:follow(f1.t, 0.5, 2))
 *
 * The browser draws the file's waveform but does not play it. An external
 * engine -- SuperCollider, Pd, Max, anything that speaks OSC -- plays it
 * (for multichannel output, spatialisation, processing) and reports where
 * it is; the cursor on the waveform shows that, not a guess.
 *
 * Oscilla → engine (the server's OSC out, default port 57120):
 *
 *     /oscilla/tape/play    uid  path  startSec  rate  loop  relPath  amp
 *     /oscilla/tape/stop    uid  fadeSec
 *     /oscilla/tape/seek    uid  sec
 *     /oscilla/tape/rate    uid  rate
 *     /oscilla/tape/pause   uid
 *     /oscilla/tape/resume  uid
 *     /oscilla/tape/amp     uid  amp
 *
 *   path is the file's absolute path on the server's machine (the server
 *   resolves it inside the project); relPath the project-relative one, for
 *   an engine on another machine with its own copy. loop: 1 plays once,
 *   0 for ever, N times. Only play and stop are required of an engine;
 *   the rest it may ignore.
 *
 * Engine → Oscilla (OSC in, default port 57121), about 20 times a second:
 *
 *     /<uid>/pos  sec  rate
 *     /<uid>/end                  (optional: it has finished)
 *
 * The cursor is drawn from those reports by tapeClock.js, each placed at
 * the moment the server received it (serverClock.js). The position is
 * published as <uid>.sec and <uid>.t (0..1), so other cues can follow the
 * sound's real place.
 *
 * Started by the playhead (a cue in the score), the tape is LOCKED to it:
 * where it should be is the playhead's distance into the element, in the
 * score's time. A big gap -- the start-up delay at first, a stall later --
 * is seeked away, a small drift played out by nudging the rate by at most
 * 0.5 % (tapeClock.js lockStep); the transport pausing pauses it, and
 * resuming puts it back where the playhead is. The cursor shows what the
 * engine reports either way, so whatever is left is in plain sight.
 *
 * latency: (ms) draws the cursor that far ahead of the reported position:
 * the display's lag minus the engine's output latency, measured once per
 * setup (a click track against marks on its waveform). Negative if the
 * picture is early.
 *
 * Several browsers run the same score: the server drops a repeat of the
 * same event for the same sound within 300 ms, so the engine is told once,
 * whichever browser sends first.
 */

import { createTapeClock, lockStep } from "./tapeClock.js";
import { sendOSC } from "../../system/oscillaOSCClient.js";
import { toLocal } from "../../system/serverClock.js";
import { publish } from "../../control/paramBinding.js";
import { startCursor, resetCursor } from "../../system/waveform.js";
import { steerFollows } from "./audioShared.js";

export const isExternalEngine = (ast, params = {}) =>
  /^(external|ext)$/i.test(String(ast?.engine ?? params.engine ?? "").trim());

// A seek takes a moment to reach the engine (browser → server → engine,
// a few ms on one machine): it aims that far ahead, and the rate nudge
// plays out whatever is left.
const SEEK_LEAD_SEC = 0.005;
const ERR_AVG_MS = 250;                // the lock's error, averaged over about this

const send = (action, fields, immediate = true) =>
  sendOSC({ type: "osc_ext_audio", action, ...fields, project: window.currentProject || null }, { immediate });

/**
 * Start the sound on the engine and draw it. Returns the voice for the
 * registry (window.activeAudioCues): stop / pause / resume / update /
 * steer, like a voice the browser plays itself, so hush(), stop cues,
 * region exits and follow() reach it the same way.
 */
export function startExternalTape({ uid, src, duration, handle, startSec = 0, rate = 1, loop = 1, amp = 1, latencyMs = 0, follows = {}, onEnded = null, lock = null }) {
  const clock = createTapeClock();
  let stopped = false, paused = false, raf = 0, unsteer = null;
  let baseRate = rate;                         // the cue's own speed (speed:, follow())
  const startedAt = performance.now();
  let lastSeekAt = -Infinity, sentRate = rate, pausedByLock = false, snapped = false;
  let errAvg = null, errAt = 0, rateSentAt = -Infinity;
  let shown = { sec: startSec, stalled: false, live: false };

  // the cursor's clock: where the engine's sound is (startCursor reads only
  // currentTime, so the waveform's own cursor needs no change)
  const engineClock = { get currentTime() { return shown.sec; } };
  if (handle) {
    startCursor(handle, engineClock, 0, duration, 1, loop);
    // where the engine is, drawn strongly: it is what a performer reads,
    // and it must stand apart from the playhead crossing the same waveform
    handle.cursor?.setAttribute("stroke-width", "3.5");
    handle.cursor?.setAttribute("opacity", "1");
  }

  const onOsc = (e) => {
    const { address, args = [], serverTime } = e.detail || {};
    if (address === `/${uid}/pos`) {
      const at = toLocal(serverTime) ?? performance.now();
      clock.report(Number(args[0]), Number(args[1] ?? 1), at);
    } else if (address === `/${uid}/end`) {
      finish();
    }
  };
  window.addEventListener("oscilla:osc-in", onOsc);

  const frame = () => {
    if (stopped) return;
    shown = clock.at(performance.now() + latencyMs);
    if (!shown.live) shown = { sec: startSec, stalled: false, live: false };
    if (handle?.cursor) {
      // no word from the engine: the cursor says so, dashed
      if (shown.stalled || !shown.live) handle.cursor.setAttribute("stroke-dasharray", "3 3");
      else handle.cursor.removeAttribute("stroke-dasharray");
    }
    if (shown.live) publish("audio", uid, { sec: shown.sec, t: duration > 0 ? shown.sec / duration : 0 });
    if (lock) keepLocked();
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  /**
   * Tell the engine to go to `sec`. The clock goes there too, from when the
   * seek can have arrived; the engine's reports confirm it, and any made
   * before it are ignored (tapeClock seekTo).
   */
  function seekEngine(sec, now) {
    send("seek", { uid, sec });
    clock.seekTo(sec, now + SEEK_LEAD_SEC * 1000);
    lastSeekAt = now;
    errAvg = null;                                 // measured afresh from here
  }

  /** Each frame: keep the tape where the playhead says it should be. */
  function keepLocked() {
    const now = performance.now();
    const playing = lock.playing();
    if (!playing) {
      if (!paused && clock.reports) { voice.pause(); pausedByLock = true; }
      return;
    }
    if (pausedByLock) {
      // the transport goes on: so does the tape, from where the playhead is
      pausedByLock = false;
      const e = lock.expected();
      if (e != null) seekEngine(Math.max(0, e + SEEK_LEAD_SEC), now);
      voice.resume();
      return;
    }
    if (paused) return;                            // paused by hand: left alone
    // both at the same instant: the engine's position and the score's
    const here = clock.at(now);
    const e = lock.expected(now);
    if (!here.live || here.stalled || e == null) return;
    const base = baseRate * lock.speed();
    const raw = e - here.sec;
    // acted on averaged over ~a quarter second: a nudge moves the tape a
    // few ms a second at most, and frame-to-frame jitter is not worth
    // chasing (the first reading counts at once, for the start-up snap)
    const k = errAvg == null ? 1 : 1 - Math.exp(-(now - errAt) / ERR_AVG_MS);
    errAvg = errAvg == null ? raw : errAvg + (raw - errAvg) * k;
    errAt = now;
    voice.lockErr = errAvg;                        // seconds behind the score (for a readout, a test)
    voice.lockRaw = raw;
    const step = lockStep({ err: errAvg, base, sinceStartMs: now - startedAt, sinceSeekMs: now - lastSeekAt, snapped });
    if (step.seek) {
      snapped = true;
      seekEngine(Math.max(0, e + SEEK_LEAD_SEC), now);
      if (Math.abs(sentRate - base) > 1e-6) { send("rate", { uid, rate: base }, false); sentRate = base; }
    } else if (Math.abs(step.rate - sentRate) > base * 0.001 && now - rateSentAt > 200) {
      send("rate", { uid, rate: step.rate }, false);
      sentRate = step.rate; rateSentAt = now;
    }
  }

  function finish() {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(raf);
    window.removeEventListener("oscilla:osc-in", onOsc);
    unsteer?.(); unsteer = null;
    clock.reset();
    if (handle) { handle.cursor?.removeAttribute("stroke-dasharray"); resetCursor(handle); }
    onEnded?.();
  }

  const voice = {
    uid,
    filename: src,
    external: true,
    analyser: null,
    stop(fadeSec = 0) {
      send("stop", { uid, fade: Number(fadeSec) || 0 });
      finish();
    },
    pause() { if (stopped || paused) return false; paused = true; send("pause", { uid }); return true; },
    resume() { if (stopped || !paused) return false; paused = false; send("resume", { uid }); return true; },
    isPaused: () => paused,
    /** A re-run of the line, or a follow(): the engine is told what changed. */
    update(p = {}) {
      if (stopped) return;
      const r = p.speed ?? p.pitch;
      if (r != null && Number.isFinite(Number(r))) {
        baseRate = Number(r);                      // the lock nudges around this
        sentRate = baseRate * (lock ? lock.speed() : 1);
        send("rate", { uid, rate: sentRate }, false);
      }
      if (p.amp != null && Number.isFinite(Number(p.amp))) send("amp", { uid, amp: Number(p.amp) }, false);
      if (p.seek != null && Number.isFinite(Number(p.seek))) seekEngine(Number(p.seek), performance.now());
    },
    steer(f) { unsteer?.(); unsteer = stopped ? null : steerFollows(f, (v) => voice.update(v)); },
  };

  send("play", { uid, src, sec: startSec, rate, loop, amp });
  voice.steer(follows);
  return voice;
}
